/** Minimal shape required for privilege merge (full RoutePrivilege is a superset). */
export interface RoutePrivilegeNode {
  id: string;
  hasAccess: boolean;
  children?: RoutePrivilegeNode[];
}

function indexHasAccessById(routes: RoutePrivilegeNode[]): Map<string, boolean> {
  const map = new Map<string, boolean>();

  const walk = (nodes: RoutePrivilegeNode[]) => {
    for (const node of nodes) {
      map.set(node.id, node.hasAccess);
      if (node.children?.length) walk(node.children);
    }
  };

  walk(routes);
  return map;
}

/**
 * Always uses template for structure. Overlays hasAccess from DB by id.
 * Routes present only in the template (not in DB) get hasAccess: false.
 * Routes present only in DB are dropped.
 */
export function mergeTemplateWithDb<T extends RoutePrivilegeNode>(
  template: T[],
  dbRoutes: RoutePrivilegeNode[]
): T[] {
  const dbAccess = indexHasAccessById(dbRoutes);

  const apply = (nodes: T[]): T[] =>
    nodes.map((node) => ({
      ...node,
      hasAccess: dbAccess.has(node.id) ? dbAccess.get(node.id)! : false,
      children: node.children?.length
        ? apply(node.children as T[])
        : node.children,
    }));

  return apply(template);
}
